// @ts-nocheck
import React, { useRef } from "react";
import { t } from "i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FILES_URL } from "includes/paths";
import Button from "components/Button/Button";
import "./VideoSender.css";

// Must match the API upload limit (documents.controller MAX_FILE_SIZE).
const MAX_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_VIDEOS = 3;
const ACCEPTED_TYPES = ".mp4,.mov,.webm";
const ACCEPTED_MIMES = ["video/mp4", "video/quicktime", "video/webm"];

export default function VideoSender({ videos = [], setPostVideos }) {
	const inputRef = useRef(null);

	function handleFileSelect(e) {
		const files = Array.from(e.target.files || []);
		const currentCount = videos.filter((v) => !v.isDeleted).length;

		let newVideos = [...videos];
		for (const file of files) {
			if (currentCount + newVideos.filter((v) => v.isNew && !v.isDeleted).length >= MAX_VIDEOS) {
				toast.warn(t("Videos.MAX_REACHED", { max: MAX_VIDEOS }));
				break;
			}
			if (!ACCEPTED_MIMES.includes(file.type)) {
				toast.error(t("Videos.INVALID_FORMAT", { name: file.name }));
				continue;
			}
			if (file.size > MAX_SIZE) {
				toast.error(t("Videos.FILE_TOO_LARGE", { name: file.name, max: MAX_SIZE / (1024 * 1024) }));
				continue;
			}

			newVideos.push({
				file: file,
				isNew: true,
				previewUrl: URL.createObjectURL(file),
				fileName: file.name,
				fileSize: file.size,
			});
		}

		setPostVideos(newVideos);
		if (inputRef.current) inputRef.current.value = "";
	}

	function handleDelete(index) {
		let newVideos = [...videos];
		if (newVideos[index].isNew) {
			if (newVideos[index].previewUrl) URL.revokeObjectURL(newVideos[index].previewUrl);
			newVideos.splice(index, 1);
		} else {
			newVideos[index] = { ...newVideos[index], isDeleted: true };
		}
		setPostVideos(newVideos);
	}

	const activeVideos = videos.filter((v) => !v.isDeleted);
	const canAdd = activeVideos.length < MAX_VIDEOS;

	function getVideoUrl(video) {
		if (video.previewUrl) return video.previewUrl;
		if (video.urlOrigin) {
			if (video.urlOrigin.startsWith("http")) return video.urlOrigin;
			return FILES_URL + video.urlOrigin;
		}
		return null;
	}

	function formatSize(bytes) {
		if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " Ko";
		return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
	}

	return (
		<div className="VideoSender">
			<div className="video-sender-header">
				<FontAwesomeIcon icon={faVideo} />
				<span>{t("Videos.ADD_VIDEO")}</span>
				<span className="video-count">{activeVideos.length}/{MAX_VIDEOS}</span>
			</div>
			<div className="video-sender-list">
				{activeVideos.map((video, i) => {
					const url = getVideoUrl(video);
					const realIndex = videos.indexOf(video);
					return (
						<div key={i} className="video-sender-item">
							{url && (
								<video
									className="video-thumbnail"
									src={url}
									preload="metadata"
									muted
								/>
							)}
							<div className="video-info">
								<span className="video-name">
									{video.fileName || video.title || "Video"}
								</span>
								{video.fileSize && (
									<span className="video-size">{formatSize(video.fileSize)}</span>
								)}
							</div>
							<Button
								className="unstyled video-delete-btn"
								onClick={() => handleDelete(realIndex)}
							>
								<FontAwesomeIcon icon={faTrash} />
							</Button>
						</div>
					);
				})}
			</div>
			{canAdd && (
				<label className="video-add-btn">
					<FontAwesomeIcon icon={faPlus} />
					<span>{activeVideos.length === 0 ? t("Videos.ADD_VIDEO") : t("Videos.ADD_ANOTHER")}</span>
					<input
						ref={inputRef}
						type="file"
						accept={ACCEPTED_TYPES}
						onChange={handleFileSelect}
						style={{ display: "none" }}
					/>
				</label>
			)}
			<div className="video-formats-hint">
				{t("Videos.SUPPORTED_FORMATS")}
			</div>
		</div>
	);
}
